import React from 'react';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="public-page">
      {/* Navigation - Premium Compact Header */}
      <nav className="public-nav">
        <div className="public-nav-container">
          <Link to="/" className="public-nav-brand">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="nav-brand-logo" 
            />
          </Link>
          <div className="public-nav-links">
            <Link to="/" className="public-nav-link">Home</Link>
            <Link to="/pricing" className="public-nav-link">Pricing</Link>
            <Link to="/login" className="public-nav-link">Log in</Link>
          </div>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <div className="methodology-page" style={{ paddingTop: '2rem' }}>
        <div className="methodology-container">
          <div className="methodology-hero">
            <p className="methodology-kicker">Legal</p>
            <h1 className="methodology-title">Privacy Policy</h1>
            <p className="methodology-subtitle">
              How Black Peak Analytics collects, uses, and protects your information
            </p>
          </div>

          {/* Last Updated */}
          <div style={{ 
            marginBottom: '2rem', 
            padding: '1rem', 
            backgroundColor: 'rgba(16, 185, 129, 0.1)', 
            borderRadius: '0.5rem',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }}>
            <p className="theme-text" style={{ margin: 0 }}>
              <strong>Last Updated:</strong> January 2025
            </p>
          </div>

          {/* Introduction */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Introduction</h2>
            <p>
              Black Peak Analytics ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, share, and protect your personal information when you use our stock research 
              and portfolio management platform.
            </p>
            <p>
              By using Black Peak Analytics, you agree to the collection and use of information in accordance with this policy. 
              If you do not agree with our policies and practices, please do not use our service.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Information We Collect</h2>
            
            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Account Information</h3>
              <p>
                When you create an account, we collect:
              </p>
              <ul className="methodology-list">
                <li>Email address (used for login and communication)</li>
                <li>Password (encrypted and securely stored)</li>
                <li>Account creation date and last login information</li>
                <li>Subscription plan type (Free, Pro, or Premium)</li>
              </ul>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Billing and Payment Information</h3>
              <p>
                Payment processing is handled entirely by Stripe, our third-party payment processor. We do not store 
                your full credit card numbers, CVV codes, or other sensitive payment details on our servers.
              </p>
              <p>
                We receive and store from Stripe:
              </p>
              <ul className="methodology-list">
                <li>Stripe customer ID (for managing your subscription)</li>
                <li>Subscription status and billing cycle information</li>
                <li>Last 4 digits of your payment method (for display purposes only)</li>
                <li>Payment method brand (e.g., Visa, Mastercard)</li>
              </ul>
              <p>
                All payment card data is stored and processed securely by Stripe in compliance with PCI-DSS standards. 
                For more information, see Stripe's Privacy Policy at{' '}
                <a 
                  href="https://stripe.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="theme-link"
                >
                  stripe.com/privacy
                </a>.
              </p>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Saved Scans and Portfolio Data</h3>
              <p>
                To provide our stock research and portfolio management services, we store:
              </p>
              <ul className="methodology-list">
                <li>Stock tickers you scan or add to portfolios</li>
                <li>Scan results, including rankings and investment scores</li>
                <li>Portfolio names and holdings (tickers, quantities, purchase prices)</li>
                <li>Alert preferences and notification settings</li>
                <li>Scan comparison history and saved reports</li>
              </ul>
              <p>
                This data is associated with your account and used solely to provide the service you've requested.
              </p>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Usage and Technical Data</h3>
              <p>
                We automatically collect certain technical information when you use our service:
              </p>
              <ul className="methodology-list">
                <li>IP address and general location information</li>
                <li>Browser type and version</li>
                <li>Device information and operating system</li>
                <li>Pages visited and features used</li>
                <li>Access times and session duration</li>
                <li>Error logs and performance data</li>
              </ul>
              <p>
                This information helps us improve our service, diagnose technical issues, and understand how users 
                interact with our platform.
              </p>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">How We Use Your Information</h2>
            <p>
              We use the information we collect to:
            </p>
            <ul className="methodology-list">
              <li>Provide, maintain, and improve our stock research and portfolio management services</li>
              <li>Process your subscription payments and manage your account</li>
              <li>Send you important service updates, security alerts, and administrative messages</li>
              <li>Respond to your support requests and customer service inquiries</li>
              <li>Generate portfolio alerts based on your preferences</li>
              <li>Analyze usage patterns to improve our platform and develop new features</li>
              <li>Detect, prevent, and address technical issues or fraudulent activity</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </ul>
            <p>
              We do not sell, rent, or share your personal information with third parties for their marketing purposes.
            </p>
          </div>

          {/* Data Security */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Data Security</h2>
            <p>
              We take reasonable measures to protect your information from unauthorized access, alteration, disclosure, 
              or destruction:
            </p>
            <ul className="methodology-list">
              <li>All data transmission is encrypted using industry-standard SSL/TLS protocols</li>
              <li>Passwords are hashed using secure cryptographic algorithms</li>
              <li>Access to user data is restricted to authorized personnel only</li>
              <li>We regularly review and update our security practices</li>
              <li>Payment processing is handled by PCI-DSS compliant third-party providers</li>
            </ul>
            <p>
              However, no method of transmission over the internet or electronic storage is 100% secure. While we 
              strive to protect your information, we cannot guarantee its absolute security.
            </p>
          </div>

          {/* Third-Party Services */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Third-Party Services</h2>
            <p>
              We use the following third-party services to operate our platform:
            </p>
            
            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Stripe (Payment Processing)</h3>
              <p>
                Stripe processes all subscription payments and stores your payment information securely. We receive 
                limited payment data from Stripe (customer ID, subscription status, last 4 card digits) to manage 
                your account. Stripe's use of your information is governed by their Privacy Policy.
              </p>
            </div>

            <div className="methodology-component-card">
              <h3 className="methodology-component-title">Financial Data Providers</h3>
              <p>
                We use third-party financial data providers to obtain stock prices, fundamentals, and market data. 
                These providers do not receive your personal information or know which stocks you're researching.
              </p>
            </div>

            <p>
              We carefully select third-party services and require them to maintain appropriate security and privacy 
              standards. However, we are not responsible for the privacy practices of these third parties.
            </p>
          </div>

          {/* Your Choices and Rights */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Your Choices and Rights</h2>
            <p>
              You have the following rights regarding your information:
            </p>
            <ul className="methodology-list">
              <li>
                <strong>Access and Update:</strong> You can view and update your account information, portfolios, 
                and alert preferences through your account dashboard
              </li>
              <li>
                <strong>Delete Data:</strong> You can delete individual scans, portfolios, or alerts at any time 
                through the platform
              </li>
              <li>
                <strong>Cancel Subscription:</strong> You can cancel your paid subscription at any time through 
                the Billing page or Stripe Customer Portal
              </li>
              <li>
                <strong>Account Deletion:</strong> You can request complete account deletion by contacting our 
                support team (see Contact section below)
              </li>
              <li>
                <strong>Data Export:</strong> You can export your scan results and portfolio data as PDF reports
              </li>
            </ul>
            <p>
              When you delete your account, we will delete your personal information and account data. However, we 
              may retain certain information as required by law or for legitimate business purposes (e.g., preventing 
              fraud, resolving disputes).
            </p>
          </div>

          {/* Data Retention */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as needed to provide you services. 
              If you cancel your subscription or delete your account, we will delete or anonymize your personal 
              information within a reasonable timeframe, except where we are required to retain it for legal, 
              regulatory, or security purposes.
            </p>
          </div>

          {/* Children's Privacy */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Children's Privacy</h2>
            <p>
              Black Peak Analytics is not intended for use by individuals under the age of 18. We do not knowingly 
              collect personal information from children. If we become aware that we have collected information from 
              a child under 18, we will take steps to delete that information promptly.
            </p>
          </div>

          {/* Changes to This Policy */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, 
              operational, or regulatory reasons. We will notify you of any material changes by posting the updated 
              policy on this page and updating the "Last Updated" date.
            </p>
            <p>
              Your continued use of Black Peak Analytics after any changes indicates your acceptance of the updated 
              Privacy Policy.
            </p>
          </div>

          {/* Contact Information */}
          <div className="methodology-section">
            <h2 className="methodology-section-title">Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div style={{ 
              marginTop: '1.5rem',
              padding: '1.5rem',
              backgroundColor: 'rgba(8, 22, 38, 0.5)',
              border: '1px solid rgba(71, 85, 105, 0.4)',
              borderRadius: '0.5rem'
            }}>
              <p style={{ margin: '0 0 1rem 0' }}>
                <strong>Email:</strong>{' '}
                <a 
                  href="mailto:support@blackpeakanalytics.com"
                  className="theme-link"
                >
                  support@blackpeakanalytics.com
                </a>
              </p>
              <p className="theme-text" style={{ margin: 0 }}>
                We will respond to your inquiry within 2-3 business days.
              </p>
            </div>
          </div>
        
        </div>
      </div>

      {/* Footer */}
      <footer className="public-footer public-footer-compact">
        <div className="public-footer-container public-footer-container-compact">
          <Link to="/" className="footer-brand-compact">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="footer-brand-logo" 
            />
          </Link>
          <nav className="footer-nav-compact" aria-label="Footer navigation">
            <Link to="/" className="public-footer-link">Home</Link>
            <Link to="/pricing" className="public-footer-link">Pricing</Link>
            <Link to="/support" className="public-footer-link">Support</Link>
            <Link to="/privacy" className="public-footer-link">Privacy</Link>
            <Link to="/terms" className="public-footer-link">Terms</Link>
            <Link to="/investment-disclaimer" className="public-footer-link">Disclaimer</Link>
          </nav>
          <p className="footer-disclaimer footer-disclaimer-compact">Independent research tool. Not financial advice.</p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
